import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { EstoqueService } from './Service';

const service = new EstoqueService();

interface EstoqueContextProps {
    items: any[];
    itemsMeta: { total: number; page: number; limit: number };
    itemsLoading: boolean;
    itemsError: boolean;
    overviewError: boolean;
    summary: any | null;
    summaryLoading: boolean;
    recommendations: any[];
    recommendationsLoading: boolean;
    reloadItems: (params?: any) => Promise<void>;
    reloadSummary: () => Promise<void>;
    reloadRecommendations: () => Promise<void>;
}

const EstoqueContext = createContext<EstoqueContextProps | undefined>(undefined);

export const useEstoqueContext = () => {
    const context = useContext(EstoqueContext);
    if (!context) {
        throw new Error('useEstoqueContext must be used within EstoqueProvider');
    }
    return context;
};

export const EstoqueProvider = ({ children }: { children: ReactNode }) => {
    const [items, setItems] = useState<any[]>([]);
    const [itemsMeta, setItemsMeta] = useState({ total: 0, page: 1, limit: 50 });
    const [itemsLoading, setItemsLoading] = useState(true);
    const [itemsError, setItemsError] = useState(false);
    const [summaryError, setSummaryError] = useState(false);
    const [recommendationsError, setRecommendationsError] = useState(false);
    const itemsRequest = useRef(0);

    const [summary, setSummary] = useState<any | null>(null);
    const [summaryLoading, setSummaryLoading] = useState(true);

    const [recommendations, setRecommendations] = useState<any[]>([]);
    const [recommendationsLoading, setRecommendationsLoading] = useState(true);

    const reloadItems = async (params: any = {}) => {
        // A busca dispara varias requisicoes: so a mais recente pode atualizar a lista.
        const request = ++itemsRequest.current;
        setItemsLoading(true);
        setItemsError(false);
        try {
            const result = await service.findItems(params).then((r) => r?.data || {});
            if (request !== itemsRequest.current) return;
            setItems(result?.data || []);
            setItemsMeta({
                total: result?.total ?? 0,
                page: result?.page ?? 1,
                limit: result?.limit ?? 50,
            });
        } catch (error) {
            console.error('Error fetching items:', error);
            if (request === itemsRequest.current) setItemsError(true);
        } finally {
            if (request === itemsRequest.current) setItemsLoading(false);
        }
    };

    const reloadSummary = async () => {
        setSummaryLoading(true);
        setSummaryError(false);
        try {
            const result = await service.getResumo().then((r) => r?.data || null);
            setSummary(result);
        } catch (error) {
            console.error('Error fetching summary:', error);
            setSummaryError(true);
        } finally {
            setSummaryLoading(false);
        }
    };

    const reloadRecommendations = async () => {
        setRecommendationsLoading(true);
        setRecommendationsError(false);
        try {
            const result = await service.getRecommendations().then((r) => r?.data || []);
            setRecommendations(result);
        } catch (error) {
            console.error('Error fetching recommendations:', error);
            setRecommendationsError(true);
        } finally {
            setRecommendationsLoading(false);
        }
    };

    useEffect(() => {
        reloadItems();
        reloadSummary();
        reloadRecommendations();
    }, []);

    return (
        <EstoqueContext.Provider
            value={{
                items,
                itemsMeta,
                itemsLoading,
                itemsError,
                overviewError: summaryError || recommendationsError,
                summary,
                summaryLoading,
                recommendations,
                recommendationsLoading,
                reloadItems,
                reloadSummary,
                reloadRecommendations,
            }}
        >
            {children}
        </EstoqueContext.Provider>
    );
};
